import { disableFragmentWarnings, gql } from '@apollo/client';
import cleanDeep from 'clean-deep';
import type { DocumentNode } from 'graphql';
import { print } from 'graphql';
import { defaults } from 'lodash';
import omitDeep from 'omit-deep';

import { capitalize } from '~/shared/helpers/string-helpers';

disableFragmentWarnings();

type PlainObject = Record<string, unknown>;
type PageInfoLike = Record<string, unknown>;

type ItemsPaginationParams = Readonly<{
  entity: string;
  fragment: DocumentNode;
  filterType?: string;
  sortType?: string;
  queryName?: string;
  fragmentName?: string;
}>;

type PaginationEntry<TItem = PlainObject> = Readonly<{
  items?: TItem[];
  pageInfo?: PageInfoLike;
}>;

type QueryMap<TItem = PlainObject> = Record<string, PaginationEntry<TItem> | undefined>;

type ReadQueryParams = Readonly<{
  query: string;
  filter?: unknown;
  nextPage: number;
  sort: string;
  perPage: number;
  isLazyLoading?: boolean;
  isRefetch?: boolean;
}>;

type UpdateQueryOptions<N, T> = Readonly<{
  fetchMoreResult: T;
  variables: N;
}>;

export const clearArrayOfItems = <T extends PlainObject>(arr: T[]): T[] =>
  defaults(
    [],
    cleanDeep(omitDeep(arr, ['__typename', '__isNew__']), {
      emptyArrays: false,
    }),
  ) as T[];

export const itemsPagination = ({
  entity,
  fragment,
  filterType,
  sortType,
  queryName,
  fragmentName,
}: ItemsPaginationParams): DocumentNode => {
  const capitalizedEntity = capitalize(entity);
  const resolvedQueryName = queryName ?? 'pagination';
  const resolvedFilterType = filterType ?? `FilterPagination${capitalizedEntity}Input`;
  const resolvedSortType = sortType ?? `SortFindMany${capitalizedEntity}Input`;
  const resolvedFragmentName = fragmentName ?? `${capitalizedEntity}Fragment`;
  const operationName = `${resolvedQueryName}${capitalizedEntity}Query`;

  const query = `
    query ${operationName}(
      $filter: ${resolvedFilterType}
      $nextPage: Int!
      $perPage: Int!
      $sort: ${resolvedSortType}
    ) {
      ${operationName}(
        filter: $filter
        perPage: $perPage
        page: $nextPage
        sort: $sort
      ) {
        items {
          ...${resolvedFragmentName}
        }
        pageInfo {
          currentPage
          perPage
          itemCount
          pageCount
          hasPreviousPage
          hasNextPage
        }
      }
    }

    ${print(fragment)}
  `;

  return gql(query);
};

const buildUpdateQuery = <
  TItem extends PlainObject = PlainObject,
  TQuery extends QueryMap<TItem> = QueryMap<TItem>,
  N = Record<string, unknown>,
>(
  query: string,
  isLazyLoading: boolean,
) => {
  return (previousQueryResult: TQuery, options: UpdateQueryOptions<N, TQuery>): TQuery => {
    const previousEntry = previousQueryResult[query];
    const nextEntry = options.fetchMoreResult[query];

    const previousItems = previousEntry?.items ?? [];
    const nextItems = nextEntry?.items ?? [];

    return {
      ...previousQueryResult,
      [query]: {
        items: isLazyLoading ? [...previousItems, ...nextItems] : nextItems,
        pageInfo: nextEntry?.pageInfo ?? {},
      },
    };
  };
};

export const readQuery = <
  TItem extends PlainObject = PlainObject,
  TQuery extends QueryMap<TItem> = QueryMap<TItem>,
  N = Record<string, unknown>,
>({
  query,
  filter,
  nextPage,
  sort,
  perPage,
  isLazyLoading = false,
}: ReadQueryParams) => {
  return {
    variables: {
      filter,
      nextPage,
      perPage,
      sort,
    },
    fetchPolicy: 'no-cache' as const,
    updateQuery: buildUpdateQuery<TItem, TQuery, N>(query, isLazyLoading),
  };
};
